<template>
  <v-app>
    <router-view />
  </v-app>
</template>

<script lang="ts">
import useCategories from '@src/composables/categories'
import { useDismissedExpertiseFeedback } from '@src/pinia/dismissedExpertiseFeedback'
import {
  defineComponent, onBeforeMount, onMounted, PropType, inject, watch
} from 'vue'
import { useBeforeInstallPromptStore } from '@src/pinia/beforeInstallPrompt'
import { useUserSettingsStore } from '@src/pinia/userSettings'
import { useFeatureTogglesStore } from '@src/pinia/featureToggles'
import { useFeatureToggles } from '@src/composables/featureToggles'
import { setPkceAuthEnabled } from '@src/utilities/auth'
import { realmSettingsKey } from '@src/realmSettingKey.type'
import useSentryUserId from '@src/composables/useSentryUserId'
import useUser from '@src/composables/user'
import { useRouter } from 'vue-router'
import useAnalytics from './composables/tracking/tracking'
import { BeforeInstallPromptEvent } from './types/beforeInstallPrompt.types'

export default defineComponent({
  name: 'App',
  props: {
    beforeInstallPromptEvent: {
      type: Event as unknown as PropType<BeforeInstallPromptEvent | undefined>,
      default: undefined
    }
  },
  setup(props) {
    // TODO: This sounds too feature specific to be handled within the root component, move this to an appropriate place
    const dismissedExpertiseFeedbackStore = useDismissedExpertiseFeedback()
    const { load: loadUserSettings } = useUserSettingsStore()
    const { loadDismissedExpertiseFeedback } = dismissedExpertiseFeedbackStore
    const { loadFeatureToggles } = useFeatureTogglesStore()
    const { pkceAuthEnabled } = useFeatureToggles()
    const { user, loadCurrentUser, setUtc } = useUser()

    watch(pkceAuthEnabled, (value) => {
      setPkceAuthEnabled(value)
    }, { immediate: true })
    const realmSettings = inject(realmSettingsKey)

    onBeforeMount(async () => {
      await loadCurrentUser()
      setUtc()
      loadFeatureToggles(realmSettings!.value.metadata.realm_id)
      loadDismissedExpertiseFeedback()
      loadUserSettings()
    })

    const beforeInstallPromptStore = useBeforeInstallPromptStore()
    onMounted(() => {
      beforeInstallPromptStore.deferredPrompt = props.beforeInstallPromptEvent
    })

    useSentryUserId()
    useCategories()
    const router = useRouter()
    const { trackMixpanelEvent } = useAnalytics()
    // Vue Router's afterEach can fire twice for the same navigation (initial
    // navigation is one common case). Track the last-fired path so we only send
    // Page Viewed once per unique URL.
    let lastTrackedFullPath: string | null = null
    router.afterEach((to) => {
      const queryValues = Object.create({})
      Object.entries(to.query).forEach(([ key, value ]) => {
        queryValues[`route_query_param_${key}`] = value
      })

      if (to.fullPath !== lastTrackedFullPath) {
        lastTrackedFullPath = to.fullPath
        trackMixpanelEvent('Page Viewed', {
          page: to.path,
          ...queryValues
        } as any)
      }
    })

    return {
      user,
      loadFeatureToggles
    }
  }
})
</script>
